import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
    Download,
    RefreshCcw,
    Shuffle,
    ChevronLeft,
    CreditCard,
    Info,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { api, resolveMediaUrl } from "@/lib/api";
import { trackPageview, trackEvent } from "@/lib/analytics";

import { drawPlate, loadImage, loadSilhouetteImage, loadPlateFont, BASE_W, BASE_H } from "@/lib/platePainter";

/** SL = województwo śląskie (city of Ruda Śląska belongs to it). */
const DEFAULT_PREFIX = "SL";

/**
 * Sanitize user input: uppercase, strip whitespace/dashes, keep only
 * A-Z 0-9. Caller decides the max length.
 */
function sanitize(str, max) {
    const clean = (str || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    return clean.slice(0, max);
}

/** Generate a plausible-looking random "SL" plate ending. */
function randomTail() {
    const letters = "ABCDEFGHJKLMNPRSTUVWXYZ"; // no I/O to avoid confusion
    const digits = "0123456789";
    const patterns = ["NNNNN", "NNNAA", "AANNN", "ANNNN", "NNAAA", "NAANN"];
    const p = patterns[Math.floor(Math.random() * patterns.length)];
    let out = "";
    for (const ch of p) {
        if (ch === "N") out += digits[Math.floor(Math.random() * digits.length)];
        else out += letters[Math.floor(Math.random() * letters.length)];
    }
    return out;
}

// ---- Page component -------------------------------------------------------

export default function PlateGenerator() {
    const [modInfo, setModInfo] = useState(null);
    const [prefix, setPrefix] = useState(DEFAULT_PREFIX);
    const [tail, setTail] = useState("12345");
    const [logoImg, setLogoImg] = useState(null);
    const [hologramImg, setHologramImg] = useState(null);
    const [eagleImg, setEagleImg] = useState(null);
    const [vintageIconImg, setVintageIconImg] = useState(null);
    const [variant, setVariant] = useState("standard");
    const [downloadingScale, setDownloadingScale] = useState(0);
    const [fontsReady, setFontsReady] = useState(false);
    const previewRef = useRef(null);
    const hiddenRef = useRef(null);

    useEffect(() => {
        trackPageview("/generator-tablic");
    }, []);

    // Persistent session id used only to de-duplicate plate-event counts on
    // the backend (see /api/plate-events). No PII, no cross-site tracking.
    const sessionIdRef = useRef(null);
    if (!sessionIdRef.current) {
        try {
            let sid = localStorage.getItem("plate_session_id");
            if (!sid) {
                sid =
                    (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
                    "plate-" + Math.random().toString(36).slice(2);
                localStorage.setItem("plate_session_id", sid);
            }
            sessionIdRef.current = sid;
        } catch {
            sessionIdRef.current = "plate-anon";
        }
    }

    const sendPlateEvent = useCallback(
        (kind, extra = {}) => {
            api.post("/plate-events", {
                kind,
                prefix: prefix || DEFAULT_PREFIX,
                tail: tail || "",
                variant,
                session_id: sessionIdRef.current,
                ...extra,
            }).catch(() => {
                /* fire-and-forget - stats never block the UX */
            });
        },
        [prefix, tail, variant],
    );

    // Fire a "generated" event once the user has settled on a plate value
    // (debounced 1.2 s). Backend also rate-limits so a single session
    // can't inflate the counter no matter how fast they type.
    useEffect(() => {
        const t = setTimeout(() => {
            if ((prefix || "").length && (tail || "").length) {
                sendPlateEvent("generated");
            }
        }, 1200);
        return () => clearTimeout(t);
    }, [prefix, tail, sendPlateEvent]);

    // Wait for the Google-Fonts "License Plate" (and Anton fallback) to be
    // fully loaded before we paint the first frame - otherwise the canvas
    // falls back to Impact and the user sees a "wrong font" flash. If the
    // admin has uploaded a custom plate font, load and register it too so
    // it becomes the top-priority family in the font stack.
    useEffect(() => {
        let cancelled = false;
        const ready = () => {
            if (!cancelled) setFontsReady(true);
        };
        const url = modInfo?.plate_font_url;
        const adminFontPromise = url
            ? loadPlateFont(url.startsWith("http")
                ? url
                : `${process.env.REACT_APP_BACKEND_URL || ""}${url}`)
            : Promise.resolve(false);
        if (typeof document !== "undefined" && document.fonts) {
            Promise.all([
                document.fonts.load('900 200px "Arklatrs"'),
                document.fonts.load('900 200px "License Plate"'),
                document.fonts.load('900 200px "Anton"'),
                adminFontPromise,
            ])
                .catch(() => {})
                .finally(ready);
        } else {
            ready();
        }
        // Hard timeout - even if the network is slow, we don't want a
        // permanent blank canvas.
        const t = setTimeout(ready, 1800);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [modInfo?.plate_font_url]);

    // Load site settings (for the plate logo).
    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/mod-info");
                setModInfo(r.data);
            } catch (e) {
                /* non-fatal */
            }
        })();
    }, []);

    // Load the logo image when URL becomes available.
    useEffect(() => {
        let cancelled = false;
        const url = modInfo?.plate_logo_url || modInfo?.logo_url || "";
        (async () => {
            if (!url) {
                setLogoImg(null);
                return;
            }
            const img = await loadImage(resolveMediaUrl(url));
            if (!cancelled) setLogoImg(img);
        })();
        return () => {
            cancelled = true;
        };
    }, [modInfo?.plate_logo_url, modInfo?.logo_url]);

    // Preload the legalization-sticker assets once. Both live in
    // /public/plate-assets so they are served straight from the frontend
    // without hitting the API. The eagle is preprocessed into a proper
    // alpha silhouette so it stands out crisply against the hologram.
    // The vintage-car icon (used for "zabytkowa" plates) already ships
    // as a transparent PNG so it loads via loadImage.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const [holo, eagle, vintage] = await Promise.all([
                loadImage("/plate-assets/holo.png"),
                loadSilhouetteImage("/plate-assets/eagle.png"),
                loadImage("/plate-assets/vintage-car.png"),
            ]);
            if (cancelled) return;
            setHologramImg(holo);
            setEagleImg(eagle);
            setVintageIconImg(vintage);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Preview canvas: repaint whenever inputs change. We render at 0.5×
    // base (still ~962×211 px) to keep the DOM lightweight while looking
    // crisp on retina screens (the container is 800 px wide max).
    const previewScale = 0.5;
    const paintPreview = useCallback(() => {
        const canvas = previewRef.current;
        if (!canvas) return;
        drawPlate(canvas, {
            prefix: prefix || DEFAULT_PREFIX,
            tail: tail || "•••••",
            logoImg,
            hologramImg,
            eagleImg,
            vintageIconImg,
            variant,
            scale: previewScale,
        });
    }, [prefix, tail, logoImg, hologramImg, eagleImg, vintageIconImg, variant]);

    useEffect(() => {
        // Repaint whenever inputs OR the plate fonts become available.
        paintPreview();
    }, [paintPreview, fontsReady]);

    // Download at requested scale (1 = 520×114 mm equivalent, 2/4/8 = HD).
    const download = async (scaleFactor) => {
        setDownloadingScale(scaleFactor);
        try {
            const canvas = hiddenRef.current;
            if (!canvas) return;
            drawPlate(canvas, {
                prefix: prefix || DEFAULT_PREFIX,
                tail: tail || "•••••",
                logoImg,
                hologramImg,
                eagleImg,
                vintageIconImg,
                variant,
                scale: scaleFactor,
            });
            const dataUrl = canvas.toDataURL("image/png");
            const a = document.createElement("a");
            const w = Math.round(BASE_W * scaleFactor);
            const h = Math.round(BASE_H * scaleFactor);
            a.href = dataUrl;
            a.download = `tablica_${variant === "zabytkowa" ? "zabytkowa_" : ""}${(prefix + tail).toLowerCase()}_${w}x${h}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            trackEvent("plate_download", { scale: scaleFactor, w, h, variant });
            sendPlateEvent("downloaded", { scale: scaleFactor, w, h, variant });
        } catch (e) {
            console.error("Plate download failed", e);
        } finally {
            setDownloadingScale(0);
        }
    };

    const maxTailForVariant = useCallback(
        (v, pfx) => {
            if (v === "zabytkowa") return 3;
            return (pfx || "").length >= 3 ? 4 : 5;
        },
        [],
    );

    const onPrefixChange = (e) => {
        // Allow only 2-3 letter regional codes; keep default "SL" but let
        // the user experiment (e.g. SLA for Bielsko-Biała) - 3-char prefixes
        // are also valid on real PL plates.
        const nextPrefix = sanitize(e.target.value, 3);
        setPrefix(nextPrefix);
        // If shrinking the tail limit (e.g. 2→3 prefix on standard plate),
        // trim any overflowing characters so the preview stays valid.
        const cap = maxTailForVariant(variant, nextPrefix);
        setTail((prev) => sanitize(prev, cap));
    };
    const onTailChange = (e) => {
        // Historic ("zabytkowa") plates: max 3 chars in the tail per the
        // user brief. Standard plates: 4 chars after a 3-letter prefix,
        // otherwise 5 chars.
        const maxTail = maxTailForVariant(variant, prefix);
        setTail(sanitize(e.target.value, maxTail));
    };
    const roll = () => {
        setPrefix("SL");
        if (variant === "zabytkowa") {
            // Vintage plates carry a shorter tail - cap the random
            // pattern at 3 chars (mix letters + digits).
            const letters = "ABCDEFGHJKLMNPRSTUVWXYZ";
            const digits = "0123456789";
            const patterns = ["NNN", "AANN", "ANN", "NAA", "NNA"];
            const p = patterns[Math.floor(Math.random() * patterns.length)].slice(0, 3);
            let out = "";
            for (const ch of p) {
                if (ch === "N") out += digits[Math.floor(Math.random() * digits.length)];
                else out += letters[Math.floor(Math.random() * letters.length)];
            }
            setTail(out);
        } else {
            setTail(randomTail());
        }
    };

    // Whenever the user switches variant, trim the tail if needed so
    // switching Standard → Zabytkowa with 5 chars doesn't leave the
    // preview overflowing (and switching back preserves what's there).
    useEffect(() => {
        setTail((prev) => sanitize(prev, maxTailForVariant(variant, prefix)));
    }, [variant, prefix, maxTailForVariant]);

    const resolutions = useMemo(
        () => [
            { label: "SD", scale: 1, w: BASE_W, h: BASE_H, desc: "Standard" },
            { label: "HD", scale: 2, w: BASE_W * 2, h: BASE_H * 2, desc: "Retina" },
            { label: "2K", scale: 4, w: BASE_W * 4, h: BASE_H * 4, desc: "Druk" },
            { label: "4K", scale: 8, w: BASE_W * 8, h: BASE_H * 8, desc: "Ultra" },
        ],
        [],
    );

    return (
        <div className="App" data-testid="plate-generator-page">
            <SEO
                modInfo={modInfo}
                title="Generator polskich tablic rejestracyjnych - Project Ruda Śląska"
                description="Zaprojektuj i pobierz własną polską tablicę rejestracyjną z wyróżnikiem SL (śląskie). Realistyczna czcionka tablicowa, efekt wytłoczenia, herb Rudy Śląskiej, eksport w kilku rozdzielczościach."
                pathname="/generator-tablic"
            />
            <Header />

            {/* pt-24 = 6rem covers the fixed header; the extra
                var(--announcement-h) accounts for the top banner when
                an announcement is active so "Powrót do strony" never
                slides under the header. */}
            <main
                className="pb-20"
                style={{ paddingTop: "calc(6rem + var(--announcement-h, 0px))" }}
            >
                <section className="max-w-[1400px] mx-auto px-6">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-[color:var(--text-secondary)] hover:text-[color:var(--primary)] text-xs uppercase tracking-[0.25em] mb-6"
                        data-testid="plate-back-home"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Powrót do strony
                    </Link>

                    <div className="flex items-start gap-4 mb-3">
                        <div className="p-3 rounded-xl bg-[color:var(--primary)]/15 border border-[color:var(--primary)]/40">
                            <CreditCard className="w-7 h-7 text-[color:var(--primary)]" />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.35em] text-[color:var(--text-secondary)] mb-1">
                                Narzędzie / Generator
                            </div>
                            <h1 className="text-3xl md:text-5xl font-black text-[color:var(--text-primary)] leading-tight">
                                Polska tablica rejestracyjna
                            </h1>
                            <p className="text-[color:var(--text-secondary)] mt-2 max-w-2xl">
                                Zaprojektuj polską (PL) tablicę rejestracyjną z
                                wyróżnikiem <b>SL</b> (śląskie) i pobierz plik
                                PNG w kilku rozdzielczościach - od SD po 4K
                                do druku.
                            </p>
                        </div>
                    </div>
                </section>

                <section className="max-w-[1400px] mx-auto px-6 mt-10 grid lg:grid-cols-[1fr_360px] gap-8">
                    {/* Preview column */}
                    <div
                        className="panel-yellow p-6 md:p-10 flex flex-col items-center"
                        data-testid="plate-preview-panel"
                    >
                        {/* 3D scene: perspective wrapper + rotate a little
                            to sell the "real object" feel. */}
                        <div
                            className="plate-scene"
                            data-testid="plate-preview-scene"
                        >
                            <div className="plate-3d">
                                <canvas
                                    ref={previewRef}
                                    data-testid="plate-preview-canvas"
                                    style={{
                                        width: "min(880px, 90vw)",
                                        height: "auto",
                                        borderRadius: "14px",
                                        display: "block",
                                    }}
                                />
                            </div>
                        </div>

                        <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-muted)] text-center">
                            520 × 114 mm • proporcje 1:1 z prawdziwą tablicą
                        </div>
                    </div>

                    {/* Controls column */}
                    <div className="space-y-6">
                        <div className="panel-yellow p-6">
                            <div className="label-eyebrow mb-4">
                                Wariant tablicy
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    onClick={() => setVariant("standard")}
                                    data-testid="plate-variant-standard"
                                    className={`text-left px-3 py-2.5 rounded-md border transition-all ${
                                        variant === "standard"
                                            ? "border-[color:var(--primary)] bg-[color:var(--primary)]/15"
                                            : "border-white/10 hover:border-[color:var(--primary)]/60 bg-black/20"
                                    }`}
                                >
                                    <div className="text-xs font-black uppercase tracking-[0.18em] text-[color:var(--text-primary)]">
                                        Standardowa
                                    </div>
                                    <div className="text-[10px] text-[color:var(--text-muted)] mt-0.5">
                                        Biała • PL
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setVariant("zabytkowa")}
                                    data-testid="plate-variant-zabytkowa"
                                    className={`text-left px-3 py-2.5 rounded-md border transition-all ${
                                        variant === "zabytkowa"
                                            ? "border-[color:var(--primary)] bg-[color:var(--primary)]/15"
                                            : "border-white/10 hover:border-[color:var(--primary)]/60 bg-black/20"
                                    }`}
                                >
                                    <div className="text-xs font-black uppercase tracking-[0.18em] text-[color:var(--text-primary)]">
                                        Zabytkowa
                                    </div>
                                    <div className="text-[10px] text-[color:var(--text-muted)] mt-0.5">
                                        Żółta • Auto retro
                                    </div>
                                </button>
                            </div>
                        </div>

                        <div className="panel-yellow p-6">
                            <div className="label-eyebrow mb-4">
                                Zawartość tablicy
                            </div>

                            <div className="grid grid-cols-[110px_1fr] gap-4 items-start">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                                        Wyróżnik
                                    </div>
                                    <input
                                        data-testid="plate-prefix-input"
                                        value={prefix}
                                        onChange={onPrefixChange}
                                        maxLength={3}
                                        className="w-full text-center font-black tracking-widest text-lg rounded-md border border-[color:var(--border-strong)] bg-[color:var(--bg-base)]/60 text-[color:var(--text-primary)] px-2 py-2 focus:outline-none focus:border-[color:var(--primary)] focus:ring-2 focus:ring-[color:var(--primary)]/25 transition-colors"
                                        placeholder="SL"
                                    />
                                    <p className="text-[10px] text-[color:var(--text-muted)] mt-1.5">
                                        Domyślnie <b>SL</b> (śląskie).
                                    </p>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                                        Numer
                                    </div>
                                    <input
                                        data-testid="plate-tail-input"
                                        value={tail}
                                        onChange={onTailChange}
                                        maxLength={maxTailForVariant(variant, prefix)}
                                        className="w-full text-center font-black tracking-widest text-lg rounded-md border border-[color:var(--border-strong)] bg-[color:var(--bg-base)]/60 text-[color:var(--text-primary)] px-2 py-2 focus:outline-none focus:border-[color:var(--primary)] focus:ring-2 focus:ring-[color:var(--primary)]/25 transition-colors"
                                        placeholder={variant === "zabytkowa" ? "H23" : "12345"}
                                    />
                                    <p className="text-[10px] text-[color:var(--text-muted)] mt-1.5">
                                        A-Z, 0-9. Maks. {maxTailForVariant(variant, prefix)} znaków.
                                    </p>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={roll}
                                data-testid="plate-random-btn"
                                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[color:var(--primary)]/15 border border-[color:var(--primary)]/40 hover:bg-[color:var(--primary)]/25 transition-colors text-[color:var(--text-primary)] font-semibold text-xs uppercase tracking-[0.25em] py-2.5 rounded-md"
                            >
                                <Shuffle className="w-4 h-4" />
                                Wylosuj SL
                            </button>
                        </div>

                        <div className="panel-yellow p-6">
                            <div className="label-eyebrow mb-4 flex items-center gap-2">
                                <Download className="w-3 h-3" /> Pobierz
                            </div>
                            <p className="text-xs text-[color:var(--text-secondary)] mb-4">
                                Eksport jako PNG (przezroczystość poza tablicą, wnętrze pełne). Wybierz rozdzielczość:
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                {resolutions.map((r) => {
                                    const active = downloadingScale === r.scale;
                                    return (
                                        <button
                                            key={r.scale}
                                            type="button"
                                            disabled={downloadingScale !== 0}
                                            onClick={() => download(r.scale)}
                                            data-testid={`plate-download-${r.scale}x`}
                                            className={`group relative overflow-hidden text-left p-3 rounded-lg border transition-all ${
                                                active
                                                    ? "border-[color:var(--primary)] bg-[color:var(--primary)]/15"
                                                    : "border-white/10 hover:border-[color:var(--primary)]/60 bg-black/20"
                                            } disabled:opacity-60`}
                                        >
                                            <div className="flex items-baseline gap-2">
                                                <div className="text-lg font-black text-[color:var(--primary)]">
                                                    {r.label}
                                                </div>
                                                <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-muted)]">
                                                    {r.desc}
                                                </div>
                                            </div>
                                            <div className="text-[11px] text-[color:var(--text-secondary)] tabular-nums">
                                                {r.w} × {r.h} px
                                            </div>
                                            {active && (
                                                <div className="absolute inset-0 bg-[color:var(--bg-base)]/40 flex items-center justify-center">
                                                    <RefreshCcw className="w-4 h-4 animate-spin text-[color:var(--primary)]" />
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="panel-yellow p-6">
                            <div className="flex items-start gap-3 text-xs text-[color:var(--text-secondary)]">
                                <Info className="w-4 h-4 mt-0.5 shrink-0 text-[color:var(--primary)]" />
                                <div>
                                    <div className="font-semibold text-[color:var(--text-primary)] mb-1">
                                        Uwagi
                                    </div>
                                    Tablica jest wyłącznie graficznym
                                    projektem fanowskim - nie służy jako
                                    dokument tożsamości ani realny znak
                                    rejestracyjny.
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Hidden high-res canvas used only during downloads. */}
                <canvas ref={hiddenRef} style={{ display: "none" }} />
            </main>

            <Footer />

            {/* Local styles for the 3D perspective effect. */}
            <style>{`
                .plate-scene {
                    perspective: 1400px;
                    padding: 12px 0 26px;
                    width: 100%;
                    display: flex;
                    justify-content: center;
                    border-radius: 14px;
                    position: relative;
                }
                .plate-3d {
                    transform: rotateX(6deg) rotateY(-3deg);
                    transform-style: preserve-3d;
                    filter:
                        drop-shadow(0 24px 30px rgba(0,0,0,0.45))
                        drop-shadow(0 6px 10px rgba(0,0,0,0.35));
                    transition: transform 0.35s ease-out, filter 0.35s ease;
                }
                .plate-3d:hover {
                    transform: rotateX(4deg) rotateY(2deg);
                }
                @media (max-width: 640px) {
                    .plate-3d {
                        transform: rotateX(4deg) rotateY(-1deg);
                    }
                }
            `}</style>
        </div>
    );
}
